import { Button, Checkbox, Drawer, Hairline, Label } from "@components";
import styles from "@styles/containers/oraculo/customer-drawer/data-tab.module.scss";
import OcdpAPI2 from "providers/OcdpAPI2";

import { useEffect, useState } from "react";
import { If } from "shared/components/control-statements";
import { CheckEdge } from "shared/components/Icons";
import { InputSearch } from "shared/components/InputSearch";

const ocdp_api = new OcdpAPI2();

export default function CustomerMarkupsDrawer({
  open,
  onClose,
  alreadyDrawer = true,
  customer,
  isSegments = false,
  execution_id,
  loadCustomer,
}) {
  const [markups, setMarkups] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedMarkups, setSelectedMarkups] = useState([]);
  const [applyMarkups, setApplyMarkups] = useState(false);
  const [appliedMarkups, setAppliedMarkups] = useState(false);
  const [timer, setTimer] = useState(5);

  useEffect(() => {
    const fetchMarkups = async () => {
      const markups = await ocdp_api.getMarkups();
      setMarkups(markups);
    };

    fetchMarkups();
  }, []);

  useEffect(() => {
    if (open) setSelectedMarkups(customer?.ocdp_tags || []);
  }, [open, customer]);

  const filteredMarkups = markups.filter((markup) => {
    if (markup.type !== "CUSTOMER") return false;

    const searchLower = search.toLowerCase();
    return markup.name.toLowerCase().includes(searchLower);
  });

  useEffect(() => {
    let interval;

    if (applyMarkups) {
      interval = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [applyMarkups]);

  useEffect(() => {
    if (!open) {
      setSelectedMarkups([]);
      setApplyMarkups(false);
      setAppliedMarkups(false);
      setTimer(5);
    }
  }, [open]);

  const saveMarkupsToExecution = async () => {
    const response = await ocdp_api.assignMarkupsToExecution(
      execution_id,
      selectedMarkups.filter((m) => m.id).map((m) => m.id),
    );

    if (response?.getCode() !== 200)
      alert({
        text: response?.message || "Erro ao salvar marcações",
        buttonText: "Ok",
      });
    else {
      alert({
        text: "Salvo com sucesso!",
        buttonText: "Ok",
      });
      onClose();
    }
  };

  const saveMarkupsToCustomer = async () => {
    const response = await ocdp_api.assignMarkupToCustomer(
      customer.id,
      selectedMarkups.filter((m) => m.id).map((m) => m.id),
    );

    if (response?.getCode() !== 200)
      alert({
        text: response?.message || "Erro ao salvar marcações",
        buttonText: "Ok",
      });
    else {
      alert({
        text: "Salvo com sucesso!",
        buttonText: "Ok",
      });
      onClose();
      loadCustomer();
    }
  };

  return (
    <Drawer
      visible={open}
      onClose={() => onClose()}
      title={appliedMarkups ? "Sucesso" : "Marcações do cliente"}
      subtitle={
        appliedMarkups ? "Alterações salvas com sucesso" : customer?.name
      }
      showBackButton={!isSegments}
      alreadyDrawer={alreadyDrawer}
      withoutTransition={alreadyDrawer}
      backButton={() => onClose()}
      footer={
        isSegments ? (
          applyMarkups ? (
            appliedMarkups ? (
              <Button className="w-100" onClick={() => onClose()}>
                Fechar
              </Button>
            ) : (
              <Button
                design="primary"
                className="w-100"
                onClick={saveMarkupsToExecution}
                disabled={timer > 0}
              >
                Confirmar {timer > 0 && `(${timer}s)`}
              </Button>
            )
          ) : (
            <Button
              design="primary"
              className="w-100"
              onClick={() => setApplyMarkups(true)}
              disabled={selectedMarkups.length === 0}
            >
              Aplicar marcação
            </Button>
          )
        ) : (
          <Button
            design="primary"
            className="w-100"
            onClick={saveMarkupsToCustomer}
          >
            Salvar
          </Button>
        )
      }
    >
      <If cond={appliedMarkups}>
        <div className="d-flex align-center justify-center flex-1">
          <CheckEdge />
        </div>
      </If>

      <If cond={!applyMarkups && !appliedMarkups}>
        <Label>
          Marcações (
          {(markups.filter((m) => m.type === "CUSTOMER") || []).length})
        </Label>
        <InputSearch
          design="two"
          placeholder="Buscar por..."
          onChange={(e) => setSearch(e.target.value)}
        />

        <div className={styles.markups}>
          {filteredMarkups.map((m) => (
            <div
              className={styles.markup}
              onClick={() =>
                setSelectedMarkups((prev) => {
                  if (prev.find((p) => p.id === m.id)) {
                    return prev.filter((p) => p.id !== m.id);
                  }
                  return [...prev, m];
                })
              }
            >
              <div className="d-flex align-center h-100">
                <div
                  className={styles.color}
                  style={{ backgroundColor: m.color }}
                />
                <Checkbox
                  readOnly
                  checked={selectedMarkups.find((p) => p.id === m.id)}
                  value={m.id}
                />
                <div className={styles.tag}>
                  <i className="fal fa-tags" />
                </div>
                <p>{m.name}</p>
              </div>
            </div>
          ))}
        </div>
      </If>

      <If cond={applyMarkups && !appliedMarkups}>
        <div className={styles.applyMarkups}>
          <svg
            width="44"
            height="44"
            viewBox="0 0 44 44"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0 22C0 9.84974 9.84974 0 22 0C34.1503 0 44 9.84974 44 22C44 34.1503 34.1503 44 22 44C9.84974 44 0 34.1503 0 22Z"
              fill="#D9D26C"
              fillOpacity="0.05"
            />
            <path
              d="M21.9996 16.3996C23.4848 16.3996 24.9092 16.9896 25.9594 18.0398C27.0096 19.09 27.5996 20.5144 27.5996 21.9996C27.5996 23.4848 27.0096 24.9092 25.9594 25.9594C24.9092 27.0096 23.4848 27.5996 21.9996 27.5996C20.5144 27.5996 19.09 27.0096 18.0398 25.9594C16.9896 24.9092 16.3996 23.4848 16.3996 21.9996C16.3996 20.5144 16.9896 19.09 18.0398 18.0398C19.09 16.9896 20.5144 16.3996 21.9996 16.3996ZM21.9996 28.3996C23.697 28.3996 25.3249 27.7253 26.5251 26.5251C27.7253 25.3249 28.3996 23.697 28.3996 21.9996C28.3996 20.3022 27.7253 18.6744 26.5251 17.4741C25.3249 16.2739 23.697 15.5996 21.9996 15.5996C20.3022 15.5996 18.6744 16.2739 17.4741 17.4741C16.2739 18.6744 15.5996 20.3022 15.5996 21.9996C15.5996 23.697 16.2739 25.3249 17.4741 26.5251C18.6744 27.7253 20.3022 28.3996 21.9996 28.3996ZM21.9996 18.7996C21.7796 18.7996 21.5996 18.9796 21.5996 19.1996V22.3996C21.5996 22.6196 21.7796 22.7996 21.9996 22.7996C22.2196 22.7996 22.3996 22.6196 22.3996 22.3996V19.1996C22.3996 18.9796 22.2196 18.7996 21.9996 18.7996ZM22.5996 24.3996C22.5996 24.2405 22.5364 24.0879 22.4239 23.9753C22.3114 23.8628 22.1587 23.7996 21.9996 23.7996C21.8405 23.7996 21.6879 23.8628 21.5753 23.9753C21.4628 24.0879 21.3996 24.2405 21.3996 24.3996C21.3996 24.5587 21.4628 24.7114 21.5753 24.8239C21.6879 24.9364 21.8405 24.9996 21.9996 24.9996C22.1587 24.9996 22.3114 24.9364 22.4239 24.8239C22.5364 24.7114 22.5996 24.5587 22.5996 24.3996Z"
              fill="#FFC741"
            />
          </svg>

          <p className={styles.title}>Atenção</p>
          <If cond={selectedMarkups.length === 1}>
            <span>
              Esta marcação será atribuída à todos os clientes que foram
              segmentados. Deseja continuar?
            </span>
          </If>
          <If cond={selectedMarkups.length > 1}>
            <span>
              As marcações serão atribuídas à todos os clientes que foram
              segmentados. Deseja continuar?
            </span>
          </If>

          <Hairline className="mt-15 mb-15" />

          <div className={styles.selectedMarkups}>
            {selectedMarkups.map((m) => (
              <div
                className={styles.selectedMarkup}
                style={{
                  backgroundColor: `rgb(from ${m?.color} r g b / 3%)`,
                }}
              >
                <div
                  className={styles.color}
                  style={{ backgroundColor: m?.color }}
                />
                <div className={styles.tag}>
                  <i className="fal fa-tags" />
                </div>
                <p>{m?.name}</p>
              </div>
            ))}
          </div>
        </div>
      </If>
    </Drawer>
  );
}
